import { useTranslation } from 'react-i18next'

import { useInlineNotifications, useTimeZone } from '../../../../hooks'
import { FormikForm } from '../../../Formik'

import {
  useRentalPortalListingsFormEntity,
  useRentalPortalListingsFormSubmit,
} from './RentalPortalListingsForm.hooks'
import {
  getHasListingPublishSection,
  getIsListingFormReadOnly,
  getListingFormInitialValues,
  getListingFormNotifications,
  getValidationSchema,
} from './RentalPortalListingsForm.utils'
import { RentalPortalListingsFormFields } from './RentalPortalListingsFormFields'

import * as styles from './RentalPortalListingsForm.module.css'

import RefreshIcon from '../../../../icons/refresh.svg'

function RentalPortalListingsForm({
  FormContainer,
  containerProps,
  createdListingId,
  initialValues,
  listingId,
  setModal,
  onCloseModal,
  onCreateRecord,
  onSuccess,
  ...restProps
}) {
  const { t } = useTranslation()

  const { handleAddNotification, handleRemoveNotification, notifications } =
    useInlineNotifications()

  const { handleReloadListing, isLoading, listing, loadError } =
    useRentalPortalListingsFormEntity(listingId)

  const validationSchema = getValidationSchema({ listingId, t })

  const timezone = useTimeZone()

  const formInitialValues = getListingFormInitialValues({
    draftValues: initialValues,
    listing,
    t,
    timezone,
  })

  const savedListingId = listingId ?? createdListingId

  const isEditMode = Boolean(savedListingId)

  const { handleDeletePhoto, handleSubmit } = useRentalPortalListingsFormSubmit(
    {
      formInitialValues,
      isEditMode,
      listing,
      listingId,
      savedListingId,
      setModal,
      onClose: containerProps.onClose,
      onCloseModal,
      onCreateRecord,
      onReloadListing: handleReloadListing,
      onRemoveNotification: handleRemoveNotification,
      onSetNotification: handleAddNotification,
      onSuccess,
    }
  )

  return (
    <FormikForm
      enableReinitialize
      Container={FormContainer}
      containerProps={containerProps}
      formProps={{
        className: styles.formBody,
      }}
      initialValues={formInitialValues}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      <RentalPortalListingsFormFields
        {...restProps}
        hasLoadError={Boolean(loadError)}
        hasPublishSection={getHasListingPublishSection({ listingId })}
        isEditMode={isEditMode}
        isLoading={isLoading}
        isReadOnly={getIsListingFormReadOnly({ isEditMode, listing })}
        listing={listing}
        listingId={savedListingId}
        notifications={getListingFormNotifications({
          loadError,
          notifications,
          retryIconProps: { Icon: RefreshIcon },
          t,
          onRetryLoad: handleReloadListing,
        })}
        onDeletePhoto={handleDeletePhoto}
        onRemoveNotification={handleRemoveNotification}
        onSetNotification={handleAddNotification}
      />
    </FormikForm>
  )
}

export default RentalPortalListingsForm
